import type { FilesFormatSchemaWithId } from '@iconik-io/iconik-api/src/services/files';
import { OBJECT_TYPE_ASSETS } from '@iconik-io/iconik-api/src/types/object_types';
import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';
import { registerFile } from '@iconik-io/shared-lib/src/uploader/file_registry';
import { subtitlesFormats } from '@iconik-io/shared-lib/src/utils/constants/files';
import { getAssetTypeFromExtension } from '@iconik-io/shared-lib/src/utils/format';

import {
  COMMON_AUDIO_EXTENSION,
  COMMON_IMAGE_EXTENSION,
  COMMON_VIDEO_EXTENSION,
} from '../constants/files';
import { setFileComponents } from '../ducks/uploader/file_components';
import type { PickedFile } from '../ducks/uploader/types';
import { ensureMimeTextCharset } from './ensure_mime_text_charset';
import { getSearchItemIconType } from './media_icon_type';
import { readFileMetadataWithLimit } from './read_file_metadata';

const getFileTypeOrFallback = (file: File) => {
  const nameParts = file.name.split('.');
  const type = nameParts?.[nameParts.length - 1];
  if (!file.type && subtitlesFormats.includes(type)) {
    return `text/${type}; charset=utf-8`;
  }

  return ensureMimeTextCharset(file.type);
};

/**
 * Keeps a file the user picked for upload and describes it. The fullpath is
 * set for files that come from a dropped directory.
 */
export const createPickedFile = (
  file: File,
  shouldReadFileMetadata = false,
  fullpath?: string,
): PickedFile => {
  // Adds type fallback for subtitle files uploaded via dropzone upload in Chrome
  const type = getFileTypeOrFallback(file);

  const formats: FilesFormatSchemaWithId[] = [
    {
      id: '',
      metadata: [{ internet_media_type: type }],
      name: 'ORIGINAL',
    },
  ];

  const fileRefId = registerFile(file);
  if (shouldReadFileMetadata) {
    setFileComponents(fileRefId, readFileMetadataWithLimit(file));
  }
  const fileParts = file.name.split('.');
  const extension =
    fileParts.length > 1 ? (fileParts.pop() ?? '').toLowerCase() : '';

  const searchItem: SearchItemEntity = {
    id: '',
    title: '',
    object_type: OBJECT_TYPE_ASSETS,
    type: getAssetTypeFromExtension(extension),
    formats,
  };

  const videoFormat = COMMON_VIDEO_EXTENSION.has(extension);
  const audioFormat = COMMON_AUDIO_EXTENSION.has(extension);
  const imageFormat = COMMON_IMAGE_EXTENSION.has(extension);

  if (videoFormat) {
    searchItem.media_type = 'video';
  } else if (audioFormat) {
    searchItem.media_type = 'audio';
  } else if (imageFormat) {
    searchItem.media_type = 'image';
  }
  return {
    fileRefId,
    name: file.name,
    size: file.size,
    type,
    fullpath,
    icon: getSearchItemIconType(searchItem),
    extension,
  };
};
